import {useState} from 'react';
import {ID, IModel} from '~/lib/model';


export type IDataSource<T, F = any> = {
    state: IDataSourceState<T, F>;
    setRecords: (records: T[]) => void;
    setRecord: (record: T) => void;
    fetchRecord: (filter?: F) => void;
    fetchRecords: (filter?: F) => void;
    createRecord: (model: T) => Promise<T>;
    updateRecord: (model: T) => Promise<T>;
    updateRecords: (model: T) => Promise<T>;
    copyRecord: (id: ID) => Promise<T>;
    deleteRecord: (model: T) => Promise<boolean>;
};

export type IDataSourceState<T, F> = {
    isPending: boolean;
    isRejected: Error | null;
    filter: F;
    records: T[];
};

const defaultDataSourceState: IDataSourceState<any, any> = {
    isPending: false,
    isRejected: null,
    filter: null,
    records: []
};

export type IDataSourceAdapter<T, F = any> = {
    fetchRecord?: (filter: F) => Promise<T>;
    fetchRecords?: (filter: F) => Promise<T[]>;
    createRecord?: (model: T) => Promise<T>;
    updateRecord?: (model: T) => Promise<T>;
    copyRecord?: (id: ID) => Promise<T>;
    updateRecords?: (model: T) => Promise<T>;
    deleteRecord?: (model: T) => Promise<boolean>;
};

export type IDataSourceOptions<T> = {
    primaryKey: keyof T;
};

const defaultDataSourceOptions: IDataSourceOptions<IModel> = {primaryKey: 'id'};

export function useDataSource<T, F>(dataSource: IDataSourceAdapter<T, F>, options?: IDataSourceOptions<T>): IDataSource<T, F> {
    let {primaryKey} = {...defaultDataSourceOptions, ...options};
    let [state, setState] = useState<IDataSourceState<T, F>>(defaultDataSourceState);
    const fetchRecord = (filter) => dataSource.fetchRecord(filter).then(result => {
        setState({...state, isRejected: null, isPending: false, records: [result]});
    }).catch(error => {
        setState({...state, isPending: false, isRejected: error});
    });
    const fetchRecords = (filter) => dataSource.fetchRecords(filter).then(result => {
        setState({...state, isRejected: null, isPending: false, records: result});
    }).catch(error => {
        setState({...state, isPending: false, isRejected: error});
    });
    const setRecord = (record): T => {
        setState({
            ...state,
            records: state.records.map(
                presentRecord => presentRecord[primaryKey] === record[primaryKey] ? record : presentRecord)
        });
        return record;
    };
    const setRecords = (records): T => {
        setState({
            ...state,
            records, isPending: false
        });
        return records;
    };
    return {
        state: state,
        fetchRecord: (filter: F) => {
            setState({...state, isPending: true, records: [], filter: filter});
            fetchRecord(filter);
        },
        fetchRecords: (filter: F) => {
            setState({...state, isPending: true, records: [], filter: filter});
            fetchRecords(filter);
        },
        setRecords: setRecords,
        setRecord: setRecord,
        updateRecord: record => dataSource.updateRecord(record).then(updatedRecord => {
            setRecord(updatedRecord);
            return updatedRecord;
        }),
        updateRecords: records => dataSource.updateRecords(records).then(updatedRecords => {
            setRecords(updatedRecords);
            return updatedRecords;
        }),
        createRecord: record => dataSource.createRecord(record).then(createdRecord => {
            setState({
                ...state, records: [...state.records, createdRecord]
            });
            return createdRecord;
        }),
        copyRecord: id => dataSource.copyRecord(id).then(newRecord => {
            setState({
                ...state, records: [...state.records, newRecord]
            });
            return newRecord;
        }),
        deleteRecord: record => dataSource.deleteRecord(record).then(() => {
            setState({
                ...state, records: state.records.filter(presentRecord => presentRecord[primaryKey] as ID !== record[primaryKey])
            });
            return true;
        })
    };
}
